import type { SearchConfigurationJson } from "./models";

export interface Option<T extends string = string> {
  value: T;
  label: string;
  description?: string;
}

export const DATA_SOURCES = [
  { value: "passio", label: "Passio food database", default: 100, max: 1000 },
  { value: "edamam_ingredients", label: "Edamam ingredients", default: 10, max: 50 },
  { value: "edamam_recipes", label: "Edamam recipes", default: 0, max: 25 },
] as const;
export type DataSourceName = (typeof DATA_SOURCES)[number]["value"];

export const TYPE_FILTERS: Option<"generic" | "branded" | "recipe">[] = [
  { value: "generic", label: "Generic" },
  { value: "branded", label: "Branded" },
  { value: "recipe", label: "Recipe" },
];
export type TypeFilter = (typeof TYPE_FILTERS)[number]["value"];

export const SOURCE_FILTERS: Option<
  "fdc" | "firebase" | "legacy" | "openfood" | "passio" | "Passio addition" | "passio-foodai"
>[] = [
  { value: "fdc", label: "USDA FoodData Central" },
  { value: "openfood", label: "Open Food Facts" },
  { value: "passio", label: "Passio" },
  { value: "Passio addition", label: "Passio addition" },
  { value: "passio-foodai", label: "Passio Food AI" },
  { value: "firebase", label: "Firebase" },
  { value: "legacy", label: "Legacy" },
];
export type SourceFilter = (typeof SOURCE_FILTERS)[number]["value"];

/** ISO 639-1 codes for `Localization-ISO` (response text translation). */
export const LANGUAGES: Option[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "nl", label: "Dutch" },
  { value: "sv", label: "Swedish" },
  { value: "da", label: "Danish" },
  { value: "no", label: "Norwegian" },
  { value: "fi", label: "Finnish" },
  { value: "pl", label: "Polish" },
  { value: "cs", label: "Czech" },
  { value: "el", label: "Greek" },
  { value: "tr", label: "Turkish" },
  { value: "ru", label: "Russian" },
  { value: "uk", label: "Ukrainian" },
  { value: "ar", label: "Arabic" },
  { value: "he", label: "Hebrew" },
  { value: "hi", label: "Hindi" },
  { value: "bn", label: "Bengali" },
  { value: "th", label: "Thai" },
  { value: "vi", label: "Vietnamese" },
  { value: "id", label: "Indonesian" },
  { value: "ms", label: "Malay" },
  { value: "tl", label: "Filipino" },
  { value: "zh", label: "Chinese" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
];

/** BCP 47 tags for `Passio-Product-Localization` (regional routing + AI context). */
export const LOCALES: Option[] = [
  { value: "en-US", label: "English (United States)" },
  { value: "en-CA", label: "English (Canada)" },
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "en-IE", label: "English (Ireland)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-NZ", label: "English (New Zealand)" },
  { value: "en-IN", label: "English (India)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "es-US", label: "Spanish (United States)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "fr-CA", label: "French (Canada)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "it-IT", label: "Italian (Italy)" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  { value: "pt-PT", label: "Portuguese (Portugal)" },
  { value: "nl-NL", label: "Dutch (Netherlands)" },
  { value: "sv-SE", label: "Swedish (Sweden)" },
  { value: "el-GR", label: "Greek (Greece)" },
  { value: "ar-SA", label: "Arabic (Saudi Arabia)" },
  { value: "ar-AE", label: "Arabic (UAE)" },
  { value: "hi-IN", label: "Hindi (India)" },
  { value: "ja-JP", label: "Japanese (Japan)" },
  { value: "ko-KR", label: "Korean (Korea)" },
  { value: "zh-CN", label: "Chinese (China)" },
  { value: "zh-TW", label: "Chinese (Taiwan)" },
  { value: "th-TH", label: "Thai (Thailand)" },
  { value: "vi-VN", label: "Vietnamese (Vietnam)" },
  { value: "id-ID", label: "Indonesian (Indonesia)" },
];

/** Suggested keys for `regionFilters` (ISO country codes; BCP 47 tags and language codes also work). */
export const REGION_CODES: Option[] = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "GB", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "SE", label: "Sweden" },
  { value: "GR", label: "Greece" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "IN", label: "India" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "CN", label: "China" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "AE", label: "United Arab Emirates" },
];

const HEX16 = /^[0-9a-f]{16}$/i;
const uniq = <T>(xs: readonly T[]) => [...new Set(xs)];

/**
 * Typed builder for the `Passio-Search-Config` header. Empty fields are omitted from the
 * serialized JSON so the server defaults apply.
 */
export class PassioSearchConfig {
  dataSources: Partial<Record<DataSourceName, number>> = {};
  regionFilters: Record<string, number> = {};
  typeFilters: TypeFilter[] = [];
  sourceFilters: SourceFilter[] = [];
  tagFilters: string[] = [];
  excludeTagFilters: string[] = [];
  ignoreWithConcernFlags: number[] = [];
  strictRegionMode: boolean | null = null;
  preferCatalogCustomerHash = "";

  constructor(init?: SearchConfigurationJson | null) {
    if (!init) return;
    for (const e of init.dataSourceConfigs ?? []) this.dataSources[e.name] = e.requestResultCount;
    this.regionFilters = { ...(init.regionFilters ?? {}) };
    this.typeFilters = [...(init.typeFilters ?? [])];
    this.sourceFilters = [...(init.sourceFilters ?? [])];
    this.tagFilters = [...(init.tagFilters ?? [])];
    this.excludeTagFilters = [...(init.excludeTagFilters ?? [])];
    this.ignoreWithConcernFlags = [...(init.ignoreWithConcernFlags ?? [])];
    this.strictRegionMode = init.strictRegionMode ?? null;
    this.preferCatalogCustomerHash = init.preferCatalogCustomerHash ?? "";
  }

  static parse(header: string | null | undefined): PassioSearchConfig {
    if (!header) return new PassioSearchConfig();
    return new PassioSearchConfig(JSON.parse(header) as SearchConfigurationJson);
  }

  clone(): PassioSearchConfig {
    return new PassioSearchConfig(this.toJSON());
  }

  toJSON(): SearchConfigurationJson {
    const out: SearchConfigurationJson = {};
    const sources = DATA_SOURCES.filter((d) => this.dataSources[d.value] !== undefined).map((d) => ({
      name: d.value,
      requestResultCount: Math.max(0, Math.min(d.max, Math.round(this.dataSources[d.value] ?? d.default))),
    }));
    if (sources.length) out.dataSourceConfigs = sources;
    const regions = Object.entries(this.regionFilters).filter(([k]) => k.trim());
    if (regions.length) {
      out.regionFilters = Object.fromEntries(regions.map(([k, v]) => [k.trim(), Math.max(1, Math.min(10, Math.round(v)))]));
    }
    if (this.typeFilters.length) out.typeFilters = uniq(this.typeFilters);
    if (this.sourceFilters.length) out.sourceFilters = uniq(this.sourceFilters);
    if (this.tagFilters.length) out.tagFilters = uniq(this.tagFilters);
    if (this.excludeTagFilters.length) out.excludeTagFilters = uniq(this.excludeTagFilters);
    if (this.ignoreWithConcernFlags.length) out.ignoreWithConcernFlags = uniq(this.ignoreWithConcernFlags);
    if (this.strictRegionMode !== null) out.strictRegionMode = this.strictRegionMode;
    if (this.preferCatalogCustomerHash.trim()) out.preferCatalogCustomerHash = this.preferCatalogCustomerHash.trim();
    return out;
  }

  isEmpty(): boolean {
    return Object.keys(this.toJSON()).length === 0;
  }

  /** The header value, or `undefined` when nothing is configured. */
  toHeaderValue(): string | undefined {
    return this.isEmpty() ? undefined : JSON.stringify(this.toJSON());
  }

  /** Human-readable problems the server would ignore or reject. */
  validate(): string[] {
    const problems: string[] = [];
    for (const d of DATA_SOURCES) {
      const v = this.dataSources[d.value];
      if (v !== undefined && (v < 0 || v > d.max)) problems.push(`${d.label}: result count must be 0-${d.max}.`);
    }
    for (const [k, v] of Object.entries(this.regionFilters)) {
      if (v < 1 || v > 10) problems.push(`Region ${k}: priority must be 1 (highest) to 10 (lowest).`);
    }
    const hash = this.preferCatalogCustomerHash.trim();
    if (hash && hash !== "self" && !HEX16.test(hash)) problems.push(`Catalog preference must be "self" or a 16-character hex hash.`);
    return problems;
  }
}

export interface PassioRequestHeadersInit {
  /** `Localization-ISO`: ISO 639-1 code that translates response text. */
  localizationIso?: string;
  /** `Passio-Product-Localization`: BCP 47 tag for regional results and AI context. */
  productLocalization?: string;
  searchConfig?: PassioSearchConfig | SearchConfigurationJson | null;
  /** `X-Passio-Product-Images`: adds `images { product, brand }` to product-lookup items. */
  productImages?: boolean;
  /** Any additional raw headers. */
  extra?: Record<string, string>;
}

export interface HeaderPreview {
  name: string;
  value: string;
  description: string;
}

export const REQUEST_HEADER_NAMES = {
  localizationIso: "Localization-ISO",
  productLocalization: "Passio-Product-Localization",
  searchConfig: "Passio-Search-Config",
  productImages: "X-Passio-Product-Images",
} as const;

/** The optional input headers every Passio call can carry, as one typed, immutable object. */
export class PassioRequestHeaders {
  readonly localizationIso?: string;
  readonly productLocalization?: string;
  readonly searchConfig: PassioSearchConfig;
  readonly productImages: boolean;
  readonly extra: Readonly<Record<string, string>>;

  constructor(init: PassioRequestHeadersInit = {}) {
    this.localizationIso = init.localizationIso?.trim() || undefined;
    this.productLocalization = init.productLocalization?.trim() || undefined;
    this.searchConfig =
      init.searchConfig instanceof PassioSearchConfig
        ? init.searchConfig.clone()
        : new PassioSearchConfig(init.searchConfig ?? undefined);
    this.productImages = Boolean(init.productImages);
    this.extra = { ...(init.extra ?? {}) };
  }

  with(overrides: PassioRequestHeadersInit): PassioRequestHeaders {
    const defined = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== undefined));
    return new PassioRequestHeaders({ ...this.toInit(), ...defined, extra: { ...this.extra, ...(overrides.extra ?? {}) } });
  }

  toInit(): PassioRequestHeadersInit {
    return {
      localizationIso: this.localizationIso,
      productLocalization: this.productLocalization,
      searchConfig: this.searchConfig.toJSON(),
      productImages: this.productImages,
      extra: { ...this.extra },
    };
  }

  /** Serializable form for storage (localStorage, DB). */
  toJSON(): PassioRequestHeadersInit {
    return this.toInit();
  }

  static fromJSON(json: unknown): PassioRequestHeaders {
    if (!json || typeof json !== "object") return new PassioRequestHeaders();
    return new PassioRequestHeaders(json as PassioRequestHeadersInit);
  }

  toHeaders(): Record<string, string> {
    const h: Record<string, string> = {};
    if (this.localizationIso && this.localizationIso !== "en") h[REQUEST_HEADER_NAMES.localizationIso] = this.localizationIso;
    if (this.productLocalization) h[REQUEST_HEADER_NAMES.productLocalization] = this.productLocalization;
    const sc = this.searchConfig.toHeaderValue();
    if (sc) h[REQUEST_HEADER_NAMES.searchConfig] = sc;
    if (this.productImages) h[REQUEST_HEADER_NAMES.productImages] = "true";
    for (const [k, v] of Object.entries(this.extra)) if (k.trim() && v !== "") h[k.trim()] = v;
    return h;
  }

  preview(): HeaderPreview[] {
    const docs: Record<string, string> = {
      [REQUEST_HEADER_NAMES.localizationIso]: "Translates response text (display layer).",
      [REQUEST_HEADER_NAMES.productLocalization]: "Regional search index + AI regional context.",
      [REQUEST_HEADER_NAMES.searchConfig]: "Data sources, region/type/source/tag filters.",
      [REQUEST_HEADER_NAMES.productImages]: "Adds product and brand image URLs.",
    };
    return Object.entries(this.toHeaders()).map(([name, value]) => ({ name, value, description: docs[name] ?? "Custom header." }));
  }
}
